import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import TextInput from 'components/Forms/TextInput/TextInput';
import { RegistrationFormType, useRegistrationFormMeta } from 'components/Pages/Registration/formMeta';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { createNumberAriaLabel } from 'helpers/accessibility/ariaLabelHelpers';
import { isCompanyTaxIdNumberRequired, isCompanyTaxNumberRequired, isDomainLocale } from 'helpers/isDomainLocale';
import { Controller, useFormContext } from 'react-hook-form';

const Company: FC = () => {
    const formProviderMethods = useFormContext<RegistrationFormType>();
    const formMeta = useRegistrationFormMeta(formProviderMethods);
    const { defaultLocale, useTaxId } = useDomainConfig();

    const isPolishLocale = isDomainLocale(defaultLocale, 'pl');
    const isCzechLocale = isDomainLocale(defaultLocale, 'cs');
    const isSlovakLocale = isDomainLocale(defaultLocale, 'sk');
    const isHungarianLocale = isDomainLocale(defaultLocale, 'hu');
    const isPortugalLocale = isDomainLocale(defaultLocale, 'pt');
    const isGermanLocale = isDomainLocale(defaultLocale, 'de');
    const isSpanishLocale = isDomainLocale(defaultLocale, 'es');
    const isAustriaLocale = isDomainLocale(defaultLocale, 'de-AT');
    const isItalianLocale = isDomainLocale(defaultLocale, 'it-IT');

    const renderCompanyNumberFormLine = () => {
        return (
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.companyNumber.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <TextInput
                                aria-label={createNumberAriaLabel(field.value)}
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.formName + '-' + formMeta.fields.companyNumber.name}
                                isTouched={isTouched}
                                label={formMeta.fields.companyNumber.label}
                                name={formMeta.fields.companyNumber.name}
                                required={!isPolishLocale && !isPortugalLocale && !isGermanLocale && !isAustriaLocale}
                                type="text"
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.companyNumber.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
        );
    };

    const renderCompanyTaxNumberFormLine = () => {
        return (
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.companyTaxNumber.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <TextInput
                                aria-label={createNumberAriaLabel(field.value)}
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.formName + '-' + formMeta.fields.companyTaxNumber.name}
                                isTouched={isTouched}
                                label={formMeta.fields.companyTaxNumber.label}
                                name={formMeta.fields.companyTaxNumber.name}
                                required={isCompanyTaxNumberRequired(defaultLocale)}
                                type="text"
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.companyTaxNumber.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>
        );
    };

    return (
        <>
            <FormLine bottomGap>
                <Controller
                    name={formMeta.fields.companyName.name}
                    render={({ fieldState: { isTouched, invalid, error }, field }) => (
                        <>
                            <TextInput
                                required
                                fieldRef={field}
                                hasError={invalid}
                                id={formMeta.getFieldId(formMeta.fields.companyName.name)}
                                isTouched={isTouched}
                                label={formMeta.fields.companyName.label}
                                name={formMeta.fields.companyName.name}
                                type="text"
                            />
                            <FormLineError
                                data-testid={formMeta.formName + '-' + formMeta.fields.companyName.name + '-error'}
                                error={error}
                                inputType="text-input"
                            />
                        </>
                    )}
                />
            </FormLine>

            {isPolishLocale && (
                <>
                    {renderCompanyTaxNumberFormLine()}
                    {renderCompanyNumberFormLine()}
                </>
            )}
            {(isCzechLocale || isSlovakLocale || isGermanLocale || isAustriaLocale) && (
                <>
                    {renderCompanyNumberFormLine()}
                    {renderCompanyTaxNumberFormLine()}
                </>
            )}
            {isSpanishLocale && <>{renderCompanyNumberFormLine()}</>}
            {isItalianLocale && (
                <>
                    {renderCompanyNumberFormLine()}
                    {renderCompanyTaxNumberFormLine()}
                </>
            )}
            {(isHungarianLocale || isPortugalLocale) && <>{renderCompanyTaxNumberFormLine()}</>}

            {useTaxId && (
                <FormLine>
                    <Controller
                        name={formMeta.fields.companyTaxIdNumber.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <>
                                <TextInput
                                    aria-label={createNumberAriaLabel(field.value)}
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={formMeta.getFieldId(formMeta.fields.companyTaxIdNumber.name)}
                                    isTouched={isTouched}
                                    label={formMeta.fields.companyTaxIdNumber.label}
                                    name={formMeta.fields.companyTaxIdNumber.name}
                                    required={isCompanyTaxIdNumberRequired(defaultLocale)}
                                    type="text"
                                />
                                <FormLineError
                                    error={error}
                                    inputType="text-input"
                                    data-testid={
                                        formMeta.formName + '-' + formMeta.fields.companyTaxIdNumber.name + '-error'
                                    }
                                />
                            </>
                        )}
                    />
                </FormLine>
            )}
        </>
    );
};

/* @component */
export default Company;
